// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what Konvo said that you have not seen yet. The Assistant DM's own read state is the
// source: Home's Konvo chat button, the corner icon and the phone rail's Ask button show the
// count, and the phone Ask panel lists the newest of them. Only a view that shows the messages
// (the Home conversation, the DM itself) acks them, so the count drops when you have seen them.

import Channels from '@app/features/channel/state/Channels';
import AskKonvo from '@app/features/konvo_ask/state/AskKonvo';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {Logger} from '@app/features/platform/utils/AppLogger';
import ReadStates from '@app/features/read_state/state/ReadStates';
import {KONVOBOT_ID} from '@konvo/constants/src/AppConstants';
import {ChannelTypes} from '@konvo/constants/src/ChannelConstants';
import * as SnowflakeUtils from '@konvo/snowflake/src/SnowflakeUtils';

const logger = new Logger('AssistantUnread');
const HISTORY_LIMIT = 30;

/** The Assistant DM, found without a request when it is already loaded. */
export function assistantChannelIdNow(): string | null {
	if (AskKonvo.channelId) return AskKonvo.channelId;
	const dm = Channels.getPrivateChannels().find(
		(channel) => channel.type === ChannelTypes.DM && channel.recipientIds.includes(KONVOBOT_ID),
	);
	return dm?.id ?? null;
}

export interface AssistantUnreadMessage {
	id: string;
	text: string;
	at: Date;
}

/** One plain line for a message: its text, else its card's title and body. */
export function assistantMessageSummary(message: Message): string {
	const embed = message.embeds[0];
	const raw =
		message.content.trim() || [embed?.title, embed?.description].filter((part) => part?.trim()).join(' · ') || '';
	return raw
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/\*\*|__|`/g, '')
		.replace(/\s+/g, ' ')
		.trim();
}

/** Konvo's messages after the given ack, oldest first. */
function botMessagesAfter(channelId: string, ackId: string | null): Array<Message> {
	void Messages.version;
	return (ChannelMessages.get(channelId)?.toArray() ?? []).filter(
		(message) => message.author.id === KONVOBOT_ID && SnowflakeUtils.compare(message.id, ackId) > 0,
	);
}

/** How many of Konvo's messages are unread (0 when the DM is not known yet). */
export function assistantUnreadCount(channelId: string | null = assistantChannelIdNow()): number {
	if (!channelId || !ReadStates.hasUnreadPrivateChannel(channelId)) return 0;
	const counted = ReadStates.getPrivateChannelUnreadCount(channelId);
	if (counted > 0) return counted;
	// The count is built from loaded messages; before they load, count what is there, at least one.
	return Math.max(1, botMessagesAfter(channelId, ReadStates.ackMessageId(channelId)).length);
}

/** The newest unread messages from Konvo, newest first. */
export function assistantUnreadMessages(
	limit: number,
	channelId: string | null = assistantChannelIdNow(),
): Array<AssistantUnreadMessage> {
	if (!channelId || !ReadStates.hasUnreadPrivateChannel(channelId)) return [];
	return botMessagesAfter(channelId, ReadStates.ackMessageId(channelId))
		.reverse()
		.map((message) => ({id: message.id, text: assistantMessageSummary(message), at: message.timestamp}))
		.filter((message) => message.text.length > 0)
		.slice(0, limit);
}

/** Mark everything in the Assistant DM read (the phone panel's Mark read). */
export function markAssistantRead(channelId: string | null = assistantChannelIdNow()): void {
	if (!channelId) return;
	const latest = ReadStates.lastMessageId(channelId) ?? ChannelMessages.get(channelId)?.toArray().at(-1)?.id;
	ReadStates.handleChannelAck({channelId, messageId: latest ?? undefined, immediate: true});
}

const loading = new Set<string>();

/** Load the DM's recent messages when it has unread ones the count and list cannot see yet. */
export function ensureAssistantUnreadLoaded(channelId: string | null = assistantChannelIdNow()): void {
	if (!channelId || loading.has(channelId) || ChannelMessages.get(channelId)?.ready) return;
	if (!ReadStates.hasUnreadPrivateChannel(channelId)) return;
	loading.add(channelId);
	void MessageCommands.fetchMessages(channelId, null, null, HISTORY_LIMIT)
		.catch((error) => logger.warn('Assistant DM history unavailable', error))
		.finally(() => loading.delete(channelId));
}
